import { inject } from '@angular/core'

import { switchMap } from 'rxjs'

import { ImportProjectStore } from '@flowx/designer/modules/projects/components/projects/import-project.store'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'
import { loadLocalFile } from '@flowx/shared/utils'

import {
  ExtendedNotification,
  NotificationActionHandler,
  NotificationActionType,
} from '../notifications.model'

type AppVersionImportActionType =
  NotificationActionType.VIEW_IMPORT | NotificationActionType.RETRY_IMPORT

/**
 * The actions an app version import notification offers. Must be called in an injection context: the
 * dependencies are resolved once, up front, so the handlers it returns stay safe to call from a
 * toast on any page as well as from the center.
 */
export const injectAppVersionImportHandlers = (): Record<
  AppVersionImportActionType,
  NotificationActionHandler
> => {
  const navigationService = inject(FlxNavigationService)
  const importProjectStore = inject(ImportProjectStore)
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

  // Opens the imported version, as the Projects page used to once an import finished.
  const viewImport = (notification: ExtendedNotification): void => {
    navigationService.navigateToWorkspaceApplicationVersion(
      notification.workspaceId,
      notification.appId,
      notification.appVersionId
    )
  }

  // Asks for the archive again, since a browser cannot re-select a file, and imports it into the
  // workspace the failed run was in, wherever the user is now; the new run reports through a
  // notification of its own.
  const retryImport = (notification: ExtendedNotification): void => {
    loadLocalFile({ accept: ['.zip'] })
      .pipe(
        switchMap((files) =>
          importProjectStore.importApplication(
            workspacesAndPermissionsStore.casUserId(),
            files[0],
            notification.workspaceId
          )
        )
      )
      .subscribe()
  }

  return {
    [NotificationActionType.VIEW_IMPORT]: viewImport,
    [NotificationActionType.RETRY_IMPORT]: retryImport,
  }
}
